import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVehicle } from "../context/VehicleContext";
import { useCamera } from "../hooks/useCamera";
import { analyzeTireWear } from "../lib/api";
import { limitMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Disc3, Camera, Upload, Sparkles, RotateCcw, AlertTriangle, ArrowRight, SlidersHorizontal, Wrench } from "lucide-react";
import { toast } from "sonner";

const POSITIONS = ["front-left", "front-right", "rear-left", "rear-right"];

const SEVERITY = {
  none: { label: "Healthy", color: "#10B981", bg: "rgba(16,185,129,0.12)" },
  mild: { label: "Mild", color: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
  moderate: { label: "Moderate", color: "#F97316", bg: "rgba(249,115,22,0.12)" },
  severe: { label: "Severe", color: "#EF4444", bg: "rgba(239,68,68,0.12)" },
};

export default function TireWear() {
  const { vehicleLabel } = useVehicle();
  const { videoRef, cameraError, start, stop, capture } = useCamera();
  const nav = useNavigate();
  const [mode, setMode] = useState("camera");
  const [img, setImg] = useState(null);
  const [position, setPosition] = useState("front-left");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (mode === "camera" && !img) start(); else stop();
    return stop;
  }, [mode, img, start, stop]);

  const onCapture = () => { const d = capture(0.85); if (d) { setImg(d); setResult(null); } };
  const onUpload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => { setImg(r.result); setResult(null); };
    r.onerror = () => toast.error("Couldn't read that image. Try another file.");
    r.readAsDataURL(f);
  };
  const retake = () => { setImg(null); setResult(null); if (mode === "camera") start(); };

  const run = async () => {
    if (!img) { toast.error("Capture or upload a tread photo first"); return; }
    setAnalyzing(true); setResult(null);
    try {
      const data = await analyzeTireWear({ image_base64: img, position });
      setResult(data);
      toast.success("Tread analyzed");
    } catch (err) { toast.error(limitMsg(err) || "AI tread read failed"); }
    finally { setAnalyzing(false); }
  };

  const sev = result && (SEVERITY[result.severity] || SEVERITY.mild);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tire-wear-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-lg bg-cyan/10 border border-cyan/20 flex items-center justify-center text-cyan"><Disc3 size={22} /></div>
        <div>
          <h1 className="font-heading font-bold tracking-tight text-2xl">Tire Wear Diagnosis</h1>
          <p className="font-mono text-[11px] text-slate-500 tracking-widest uppercase">{vehicleLabel || "No vehicle"} · AI tread analysis</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          <div className="flex gap-2">
            <button data-testid="tire-mode-camera" onClick={() => { setMode("camera"); setImg(null); setResult(null); }}
              className={`flex-1 py-2.5 rounded-lg font-mono text-xs uppercase tracking-widest transition-all ${mode === "camera" ? "bg-cyan text-ink font-bold" : "bg-elevated text-slate-400 border border-steel"}`}>
              <Camera size={14} className="inline mr-1" /> Camera
            </button>
            <button data-testid="tire-mode-upload" onClick={() => { setMode("upload"); setImg(null); setResult(null); }}
              className={`flex-1 py-2.5 rounded-lg font-mono text-xs uppercase tracking-widest transition-all ${mode === "upload" ? "bg-cyan text-ink font-bold" : "bg-elevated text-slate-400 border border-steel"}`}>
              <Upload size={14} className="inline mr-1" /> Upload
            </button>
          </div>

          <div className="rounded-md bg-surface border border-steel p-4">
            <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">Tire position</div>
            <div className="grid grid-cols-2 gap-2">
              {POSITIONS.map((p) => (
                <button key={p} data-testid={`tire-pos-${p}`} onClick={() => setPosition(p)}
                  className={`py-2 rounded-lg font-mono text-[11px] uppercase tracking-widest transition-all ${position === p ? "bg-cyan text-ink font-bold" : "bg-elevated text-slate-400 border border-steel"}`}>
                  {p.replace("-", " ")}
                </button>
              ))}
            </div>
          </div>

          <div className="relative bg-ink rounded-md border border-steel overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
            <div className="absolute inset-0 tactical-grid opacity-30" />
            {img ? (
              <img src={img} alt="tread" className="absolute inset-0 w-full h-full object-cover" data-testid="tire-image" />
            ) : mode === "camera" ? (
              <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="tire-camera" />
            ) : (
              <label data-testid="tire-dropzone" className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:text-cyan transition-colors">
                <Upload size={36} />
                <span className="font-mono text-xs tracking-widest uppercase mt-3">Tap to upload tread photo</span>
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="tire-upload-input" />
              </label>
            )}
            {cameraError && mode === "camera" && !img && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-ink/85">
                <AlertTriangle size={30} className="text-amber" />
                <p className="font-mono text-[11px] text-slate-300 mt-3 max-w-xs leading-relaxed">{cameraError}</p>
                <Button onClick={start} className="mt-3 bg-amber text-ink font-mono text-[11px] uppercase tracking-widest font-bold"><Camera size={14} /> Retry</Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            {mode === "camera" && !img ? (
              <Button data-testid="tire-capture" onClick={onCapture} disabled={!!cameraError} className="col-span-2 bg-cyan text-ink hover:bg-cyan-hover font-mono text-xs uppercase tracking-widest font-bold">
                <Camera size={15} /> Capture Tread
              </Button>
            ) : img ? (
              <>
                <Button data-testid="tire-retake" onClick={retake} variant="outline" className="bg-elevated border-steel text-slate-300 hover:bg-steel font-mono text-xs uppercase tracking-widest">
                  <RotateCcw size={14} /> Retake
                </Button>
                <Button data-testid="tire-analyze" onClick={run} disabled={analyzing} className="bg-sky text-white hover:bg-sky/80 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                  <Sparkles size={14} /> {analyzing ? "Reading…" : "AI Diagnose"}
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <div className="lg:col-span-7">
          {!result ? (
            <div className="h-full rounded-md border border-dashed border-steel bg-surface/40 flex flex-col items-center justify-center text-center p-10" data-testid="tire-empty">
              <Disc3 size={44} className="text-slate-700" />
              <p className="font-mono text-xs text-slate-500 tracking-widest uppercase mt-4">Capture a tread photo, then AI Diagnose</p>
              <p className="text-sm text-slate-500 mt-2 max-w-sm">We'll read the wear pattern and tell you the likely camber/toe cause and the fix.</p>
            </div>
          ) : (
            <div className="rounded-md bg-surface border border-steel p-6 space-y-5" data-testid="tire-result">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase">Wear pattern</div>
                  <div className="font-heading font-bold text-2xl capitalize">{(result.pattern || "unknown").replace(/-/g, " ")}</div>
                </div>
                {sev && (
                  <span className="px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest" style={{ color: sev.color, background: sev.bg, border: `1px solid ${sev.color}55` }} data-testid="tire-severity">
                    {sev.label}
                  </span>
                )}
              </div>

              <div className="rounded-md bg-ink/50 border border-steel p-4">
                <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-1">Likely cause</div>
                <p className="text-sm text-slate-200">{result.likely_cause || "—"}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md bg-ink/50 border border-steel p-4">
                  <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-1">Camber</div>
                  <div className="font-mono text-cyan text-sm uppercase">{result.camber_hint || "—"}</div>
                </div>
                <div className="rounded-md bg-ink/50 border border-steel p-4">
                  <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-1">Toe</div>
                  <div className="font-mono text-cyan text-sm uppercase">{result.toe_hint || "—"}</div>
                </div>
              </div>

              <div className="rounded-md bg-pass/5 border border-pass/30 p-4">
                <div className="font-mono text-[10px] text-pass tracking-widest uppercase mb-1 flex items-center gap-1"><Wrench size={12} /> Recommendation</div>
                <p className="text-sm text-slate-200">{result.recommendation || "—"}</p>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">
                  Confidence {result.confidence != null ? Math.round(result.confidence * 100) : "—"}%
                </span>
                <div className="flex gap-2">
                  <Button data-testid="tire-goto-calc" onClick={() => nav("/calculator")} variant="outline" className="bg-elevated border-steel text-slate-300 hover:bg-steel font-mono text-[11px] uppercase tracking-widest">
                    <SlidersHorizontal size={13} /> Calculator
                  </Button>
                  <Button data-testid="tire-goto-guide" onClick={() => nav("/guide")} className="bg-cyan text-ink hover:bg-cyan-hover font-mono text-[11px] uppercase tracking-widest font-bold">
                    Fix Guide <ArrowRight size={13} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
